import { globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import { addCss, hide, removeCss, show } from '@shared/easyfy.utils';
import basketService from '@webServices/basket.service';
import { animationEnded } from '@clientShared/utils';
import { SALES_UNITS_CLOSE } from '@clientWebModules/salesunitselector';
import Toggle from '@clientShared/toggle';
import MessageService from '@clientWebServices/message.service';
import responseUtils from '@shared/easyfy.response.utils';
import { updateBasketAction } from '@clientShared/basket/basketactions';
import { AddToCartState } from '@shared/easyfy.enums';
import { SnackbarSeverity } from '@clientWebModules/snackbar';
import {
  BasketActionsType,
  disableBasketAction,
  enableBuyButton,
  getBasketButton,
  setBuyButtonFailState,
  setBuyButtonProcessingState,
  setBuyButtonSuccessState,
} from '@clientShared/basket/baskethelper';
import { logCustomMessage } from '@shared/easyfy.logger';

const SALES_UNIT_SELECTOR_SUCCESS_CLOSE_DELAY = 1500;

const FAVOURITES_SPINNER_DELAY = 200;

@invoke
export default class Basket {
  static Instance: Basket;
  private miniBasketPartial: string | undefined;

  constructor() {
    globalEvent({
      type: 'click',
      events: [
        {
          action: 'add-to-cart',
          callback: this.onAddToCart,
        },
        {
          action: 'basketrow-change',
          callback: this.onBasketRowChange,
        },
        {
          action: 'basketrow-remove',
          callback: this.onBasketRowRemove,
        },
        {
          event: 'toggle-mini-basket',
          callback: this.showMiniBasket,
        },
      ],
    });

    globalEvent({
      type: 'change',
      event: {
        action: 'basketrow-set',
        callback: this.onBasketRowSet,
      },
    });
  }

  onAddToCart = (e: Event) => {
    const element = e.target;
    if (!(element instanceof HTMLElement)) return;

    let spinnerRevealTimeout: number | undefined;
    const clearSpinnerRevealTimeout = () => {
      window.clearTimeout(spinnerRevealTimeout);
      spinnerRevealTimeout = undefined;
    };

    return updateBasketAction(
      {
        onBeforeUpdate: (element) => {
          if (element.dataset.state === AddToCartState.Processing) return;

          setBuyButtonProcessingState(element);

          // Only for favourites for now, this may be used for all animations later
          if (this.isFromFavourites(element)) {
            element.dataset.hideSpinner = 'true';
            spinnerRevealTimeout = window.setTimeout(() => delete element.dataset.hideSpinner, FAVOURITES_SPINNER_DELAY);
          }
        },
        onUpdateBasket: (data, token) => {
          return basketService.UpdateBasket(data, token);
        },
        onSuccessRender: (html) => Basket.Instance.renderBasket(html),
        onAfterSuccess: (element) => {
          clearSpinnerRevealTimeout();
          delete element.dataset.hideSpinner;

          const searchProductId = this.getSearchProductId(element);
          if (this.isSubscription(element) && searchProductId) {
            this.updateSubscriptionFlyout(searchProductId);
          }

          this.closeSalesUnitSelectorIfFromFavourites(element);

          this.addMiniCartAnimation();
          this.openMiniBasket(element);

          const showNotice = !this.shouldOpenMiniBasket();
          this.handleShippingStatus(showNotice);

          setBuyButtonSuccessState(element);
          enableBuyButton(element);
        },
        onError: (element) => {
          clearSpinnerRevealTimeout();
          delete element.dataset.hideSpinner;

          setBuyButtonFailState(element);
          enableBuyButton(element);
        },
      },
      element,
      BasketActionsType.Add
    )(e);
  };

  onBasketRowChange = (e: Event) => {
    return updateBasketAction(
      {
        onBeforeUpdate() {
          disableBasketAction(e);
        },
        onUpdateBasket: async (data, token) => {
          return await basketService.UpdateBasket(data, token);
        },
        onSuccessRender: (html) => Basket.Instance.renderBasket(html),
        onAfterSuccess: () => {
          this.handleShippingStatus(false);
        },
      },
      getBasketButton(e),
      BasketActionsType.Change
    )(e);
  };

  onBasketRowSet = (e: Event) => {
    return updateBasketAction(
      {
        onUpdateBasket: (data, token) => {
          return basketService.UpdateBasket(data, token);
        },
        onSuccessRender: (html) => Basket.Instance.renderBasket(html),
      },
      getBasketButton(e),
      BasketActionsType.Set
    )(e);
  };

  onBasketRowRemove = (e: Event) => {
    const basketButton = getBasketButton(e);
    return updateBasketAction(
      {
        onUpdateBasket: async (data, token) => {
          return await basketService.UpdateBasket(data, token);
        },
        onSuccessRender: (html) => Basket.Instance.renderBasket(html),
        onAfterSuccess: () => {
          this.handleShippingStatus(false);
          const freqSelector = document.querySelector('[data-container="subscription-freq"]');
          if (freqSelector) {
            window.dispatchEvent(new CustomEvent('update-subs-flyout', { bubbles: true, cancelable: false }));
          }
        },
      },
      basketButton,
      BasketActionsType.Remove
    )(e);
  };

  getBasket = async () => {
    try {
      const response = await basketService.GetBasket();

      if (responseUtils.isValidClientResponse(response)) {
        Basket.Instance.renderBasket(response.data.content, true);
      } else {
        response.data?.message != null
          ? MessageService.Instance.displayErrorMessage(response.data.message)
          : MessageService.Instance.displayDefaultErrorMessage();

        logCustomMessage('Unexpected response from getBasket', {
          action: 'getBasket',
          responseData: response.data,
          responseMessage: response.data?.message,
          responseContent: response.data?.content,
          stack: new Error().stack ?? '',
        });
      }
    } catch (error) {
      MessageService.Instance.displayDefaultErrorMessage();
      logCustomMessage('Error in getBasket', {
        action: 'getBasket',
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? (error.stack ?? '') : '',
      });
    }

    return false;
  };

  renderBasket = (responseContent: any, renderMiniBasket: boolean = true) => {
    const priceElements = document.querySelectorAll('[data-container="basket-price"]');
    const quantityElements = document.querySelectorAll('[data-container="basket-quantity"]');
    const headerCheckout = document.querySelector('[data-container="header-checkout"]');

    this.miniBasketPartial = responseContent.htmlPartial;
    if (responseContent.totalQuantity > 0) {
      localStorage.setItem('basket-quantity', responseContent.totalQuantity.toString());
      localStorage.setItem('basket-price', responseContent.prices?.priceIncVatExclDiscountExclShipping);

      priceElements.forEach((el) => {
        el.innerHTML = responseContent.prices?.priceIncVatExclDiscountExclShipping;
      });

      quantityElements.forEach((el) => {
        el.innerHTML = responseContent.totalQuantity;
        show(el, 'flex');
      });

      if (headerCheckout) {
        headerCheckout.removeAttribute('disabled');
      }
    } else {
      localStorage.setItem('basket-quantity', '0');
      localStorage.removeItem('basket-price');

      quantityElements.forEach((el) => {
        hide(el);
      });

      priceElements.forEach((el) => {
        if (el instanceof HTMLElement) el.innerHTML = el.dataset.basketText ?? '';
      });

      if (headerCheckout) {
        headerCheckout.setAttribute('disabled', 'disabled');
      }
    }
    if (renderMiniBasket) {
      this.renderMiniBasket();
    }

    return false;
  };

  showMiniBasket = async (e: Event, el: HTMLElement) => {
    if (this.miniBasketPartial) {
      this.renderMiniBasket();
    } else {
      await this.getBasket();
    }

    Toggle.Instance.onToggleElement(e, el, false);
  };

  private renderMiniBasket = () => {
    if (!this.miniBasketPartial) return;
    const basketContentEl = document.querySelector<HTMLElement>('.basket-content');
    const scrollPos = basketContentEl?.scrollTop;

    const miniBasket = document.querySelector<HTMLElement>('[data-container="mini-basket"]');
    if (!miniBasket) return;

    miniBasket.innerHTML = this.miniBasketPartial;

    const basketContentElNew = document.querySelector<HTMLElement>('.basket-content');
    if (scrollPos && basketContentElNew && basketContentElNew.scrollTop !== scrollPos) {
      basketContentElNew.scrollTo(0, scrollPos);
    }
  };

  private updateSubscriptionFlyout = (searchProductId: string) => {
    const flyoutCloseBtn = document.querySelector<HTMLElement>(`[data-target="product-options-selector-subscription-${searchProductId}"]`);
    flyoutCloseBtn?.click();

    const freqSelector = document.querySelector<HTMLElement>('[data-container="subscription-freq"]');
    const updateFreqFlyout = freqSelector?.dataset?.updateflyout;

    if (updateFreqFlyout && updateFreqFlyout.toLowerCase() === 'true') {
      const updateFlyoutEvent = new CustomEvent('update-subs-flyout', {
        bubbles: true,
        cancelable: false,
      });
      document.dispatchEvent(updateFlyoutEvent);
    }
  };

  private addMiniCartAnimation = () => {
    const headerBaskets = document.querySelectorAll('[data-container="header-basket"]');
    headerBaskets.forEach((item) => {
      if (item instanceof HTMLElement) {
        addCss(item, 'bounce');
        animationEnded(item, () => {
          removeCss(item, 'bounce');
        });
      }
    });
  };

  private getSearchProductId = (element: HTMLElement) => element?.dataset?.searchproductid;

  private isSubscription = (element: HTMLElement) => element?.dataset?.optionid !== undefined;

  private isFromFavourites = (element: HTMLElement): boolean => {
    const originatedFromFavourites =
      element.closest('[data-container="favourites-flyout"]') ?? document.querySelector('[data-container="favourites-flyout"][data-toggle-open]');
    return !!originatedFromFavourites;
  };

  // Closes the sales-unit selector when "Add to cart" is clicked on a product card that belongs to the favourites-flyout.
  private closeSalesUnitSelectorIfFromFavourites = (element: HTMLElement): void => {
    if (!this.isFromFavourites(element)) return;

    setTimeout(() => {
      document.dispatchEvent(new CustomEvent(SALES_UNITS_CLOSE));
    }, SALES_UNIT_SELECTOR_SUCCESS_CLOSE_DELAY);
  };

  private openMiniBasket = (element: HTMLElement): void => {
    if (!this.shouldOpenMiniBasket()) return;

    const isFavouritesFlyout = this.isFromFavourites(element);

    // For favourites, closeSalesUnitSelectorIfFromFavourites already closes it once the success
    // animation has had time to play. Closing it here immediately would cut that animation short.
    if (!isFavouritesFlyout) {
      document.dispatchEvent(new CustomEvent(SALES_UNITS_CLOSE));
    }

    setTimeout(
      () => {
        const toggleMiniBasketButton = document.querySelector('[data-event="toggle-mini-basket"]');
        if (!(toggleMiniBasketButton instanceof HTMLElement)) return;
        toggleMiniBasketButton.click();

        Toggle.Instance.lastTriggerElement = element;
      },
      isFavouritesFlyout ? SALES_UNIT_SELECTOR_SUCCESS_CLOSE_DELAY : 200
    );
  };

  /**
   * Checks whether the mini basket should open, based on the number of items in the basket.
   * Returns true only if the basket element exists and its quantity equals 1.
   */
  private shouldOpenMiniBasket = (): boolean => {
    const el = document.querySelector<HTMLElement>('[data-container="basket-quantity"]');
    if (!el) return false;

    const count = Number.parseInt(el.textContent?.trim() ?? '', 10);
    return count === 1;
  };

  private handleShippingStatus = (showNotice: boolean): void => {
    const el = document.querySelector('[data-container="shipping-status"]');
    if (!(el instanceof HTMLElement)) return;

    const { shippingStatusAchieved, shippingStatusAchievedMessage, shippingStatusRemainingMessage } = el.dataset;

    const achieved = (shippingStatusAchieved ?? '').toLowerCase() === 'true';
    const achievedMessage = (shippingStatusAchievedMessage ?? '').trim();
    const remainingMessage = (shippingStatusRemainingMessage ?? '').trim();

    const message = achieved ? achievedMessage : remainingMessage;
    if (!message) return;

    const key = 'FSMessageDisplayed';
    let hasNotified = localStorage.getItem(key) === 'true';

    const iconElement = el.querySelector('.icon');
    const icon = this.getIconClass(iconElement);

    // If we dropped below the threshold -> clear the flag
    // (so we can show the message again next time we cross upward)
    if (!achieved && hasNotified) {
      localStorage.removeItem(key);
      hasNotified = false;
    }

    // Display the message only once when the threshold is crossed upwards.
    // Conditions:
    // - showNotice must be true
    // - The free shipping threshold must have been reached (achieved === true)
    // - The user must not have been notified previously
    const shouldDisplay = showNotice && achieved && !hasNotified;

    if (shouldDisplay) {
      MessageService.Instance.displayMessage(SnackbarSeverity.notice, message, icon);

      // Mark as notified so it won't be shown again until we drop below the threshold
      localStorage.setItem(key, 'true');
    }
  };

  private getIconClass(el: Element | null): string | undefined {
    if (!(el instanceof HTMLElement)) return undefined;
    return Array.from(el.classList).find((c) => c.startsWith('icon-'));
  }
}
